import {
  auth,
  db
} from '../../config/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updateProfile,
  User,
  UserCredential,
  onAuthStateChanged,
  Unsubscribe,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { UserRole } from '../../types/roles';

/**
 * User Profile stored in Firestore
 * Supplements Firebase Auth user data with multi-role support and clinic/patient info
 */
export interface UserProfile {
  uid: string;

  // Multi-role support (NEW)
  roles: UserRole[];           // Array of roles user has (e.g., ['provider', 'hr_admin'])
  primaryRole: UserRole;       // Default role for dashboard display

  // Legacy single role field (DEPRECATED - kept for backward compatibility)
  // Will be migrated to roles array
  role?: 'provider' | 'client';

  email?: string;
  phoneNumber?: string;
  displayName: string;
  clinicId?: string;           // For providers
  patientId?: string;          // For clients (links to patients collection)
  hasSeenWelcome?: boolean;    // Client portal welcome modal dismissed
  createdAt: Timestamp;
  lastLogin: Timestamp;
  isActive: boolean;
}

/**
 * FirebaseAuthService
 * Handles all authentication operations for MyClinicCore
 *
 * Features:
 * - Email/Password authentication for providers
 * - Phone/SMS authentication for clients
 * - User profile management in Firestore
 * - Role-based access control
 * - Session management
 */
class FirebaseAuthService {
  private recaptchaVerifier: RecaptchaVerifier | null = null;

  /**
   * Initialize reCAPTCHA verifier for phone authentication
   * Must be called before sending phone OTP
   */
  initRecaptcha(containerId: string = 'recaptcha-container'): RecaptchaVerifier {
    if (!this.recaptchaVerifier) {
      this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: (_response: any) => {
          // reCAPTCHA solved
        },
        'expired-callback': () => {
          this.recaptchaVerifier = null;
        }
      });
    }
    return this.recaptchaVerifier;
  }

  /**
   * Send OTP to phone number (for client login)
   * @param phoneNumber - Full phone number with country code (e.g., +1234567890)
   * @returns ConfirmationResult to verify OTP
   */
  async sendPhoneOTP(phoneNumber: string): Promise<ConfirmationResult> {
    try {
      // Ensure phone number has country code
      const formattedPhone = phoneNumber.startsWith('+')
        ? phoneNumber
        : `+1${phoneNumber.replace(/\D/g, '')}`;

      // Initialize reCAPTCHA if not already done
      const appVerifier = this.initRecaptcha();

      // Send OTP via Firebase
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        appVerifier
      );

      return confirmationResult;
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      throw new Error(`Failed to send OTP: ${error.message}`);
    }
  }

  /**
   * Verify OTP code (for client login)
   * @param confirmationResult - Result from sendPhoneOTP
   * @param otpCode - 6-digit code from SMS
   * @returns UserCredential with user info
   */
  async verifyPhoneOTP(
    confirmationResult: ConfirmationResult,
    otpCode: string
  ): Promise<UserCredential> {
    try {
      const userCredential = await confirmationResult.confirm(otpCode);
      const user = userCredential.user;

      // Look up the patient record by phone number to get their real name
      let patientName: string | null = null;
      let patientId: string | undefined;

      if (user.phoneNumber) {
        try {
          const token = await user.getIdToken();
          const response = await fetch('/api/lookup-patient-by-phone', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ phoneNumber: user.phoneNumber }),
          });
          const data = await response.json();
          if (data.found) {
            patientName = data.displayName;
            patientId = data.patientId;

            // Update Firebase Auth display name
            if (patientName && !user.displayName) {
              await updateProfile(user, { displayName: patientName });
            }
          }
        } catch (lookupError) {
          console.warn('Patient phone lookup failed (non-critical):', lookupError);
        }
      }

      // Create user profile first (if doesn't exist) — must come before updateLastLogin
      await this.ensureUserProfile(user, 'client', patientName || undefined, patientId);

      // Update last login (now the document exists)
      await this.updateLastLogin(user.uid);

      return userCredential;
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      throw new Error(`Invalid verification code: ${error.message}`);
    }
  }

  /**
   * Login with email and password (for providers and clients)
   * @param email - User email address
   * @param password - User password
   * @param requiredRole - Optional role validation ('provider' or 'client')
   * @returns UserCredential with user info
   */
  async loginWithEmail(email: string, password: string, requiredRole?: 'provider' | 'client'): Promise<UserCredential> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Update last login
      await this.updateLastLogin(userCredential.user.uid);

      // Verify user role if required
      const profile = await this.getUserProfile(userCredential.user.uid);

      // Staff roles allowed on provider login page
      const STAFF_ROLES: UserRole[] = ['provider', 'system_admin', 'clinical_director', 'administrative_staff', 'hr_admin'];

      if (!profile) {
        // Allow login even without profile - we'll create it on first login
      } else if (requiredRole) {
        // For provider login, allow any staff role
        const allowedRoles = requiredRole === 'provider' ? STAFF_ROLES : [requiredRole];
        const hasRequiredRole = profile.roles
          ? profile.roles.some((r: UserRole) => allowedRoles.includes(r))
          : allowedRoles.includes(profile.role as UserRole);

        if (!hasRequiredRole) {
          if (requiredRole === 'client') {
            // For client login, check if there's a matching patient record by email
            // If so, this is a legitimate client — add client role to their profile
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const patientQuery = query(
              collection(db, 'patients'),
              where('email', '==', userCredential.user.email)
            );
            const patientSnap = await getDocs(patientQuery);
            if (!patientSnap.empty) {
              // Patient record exists — add client role
              const docRef = doc(db, 'users', userCredential.user.uid);
              const existingRoles: UserRole[] = (profile.roles || []).filter(Boolean);
              if (profile.role && !existingRoles.includes(profile.role as UserRole)) {
                existingRoles.push(profile.role as UserRole);
              }
              if (!existingRoles.includes('client')) {
                existingRoles.push('client');
              }
              const patientDocId = patientSnap.docs[0].id;
              const updateData: Record<string, any> = {
                roles: existingRoles,
                role: 'client',
                primaryRole: 'client',
              };
              if (patientDocId) {
                updateData.patientId = patientDocId;
              }
              await setDoc(docRef, updateData, { merge: true });
              // Role fixed — allow login to proceed
            } else {
              await this.logout();
              throw new Error('Access denied. Client credentials required.');
            }
          } else {
            await this.logout();
            throw new Error(`Access denied. Staff credentials required.`);
          }
        }
      }

      return userCredential;
    } catch (error: any) {
      console.error('Error logging in:', error);

      // Friendly error messages
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('This account has been disabled. Contact support.');
      } else {
        throw new Error(error.message || 'Login failed. Please try again.');
      }
    }
  }

  /**
   * Register new provider account
   * @param email - Provider email
   * @param password - Provider password
   * @param displayName - Provider full name
   * @param clinicId - Clinic ID (optional)
   * @returns UserCredential
   */
  async registerProvider(
    email: string,
    password: string,
    displayName: string,
    clinicId?: string,
    role: UserRole = 'provider',
    /**
     * Skip client-side profile creation. Invite signups pass true and let
     * /api/redeem-invite write the profile with the Admin SDK — clients can no
     * longer write users/{uid}, and the roles must come from the invite record
     * rather than from anything the client controls.
     */
    skipProfile: boolean = false
  ): Promise<UserCredential> {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name
      await updateProfile(userCredential.user, { displayName });

      if (!skipProfile) {
        // Create user profile in Firestore
        await this.createUserProfile(userCredential.user, role, clinicId);
      }

      return userCredential;
    } catch (error: any) {
      console.error('Error registering provider:', error);

      if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password must be at least 6 characters long.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      } else {
        throw new Error(error.message || 'Registration failed. Please try again.');
      }
    }
  }

  /**
   * Register a new client with email and password
   */
  async registerClient(
    email: string,
    password: string,
    displayName: string,
    patientId?: string
  ): Promise<UserCredential> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName });
      await this.createUserProfile(userCredential.user, 'client', undefined, patientId);
      return userCredential;
    } catch (error: any) {
      console.error('Error registering client:', error);
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('An account with this email already exists.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password must be at least 6 characters long.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      } else {
        throw new Error(error.message || 'Registration failed. Please try again.');
      }
    }
  }

  /**
   * Send password reset email
   * @param email - User email address
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      console.error('Error sending password reset:', error);

      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address format.');
      } else {
        throw new Error('Failed to send password reset email. Please try again.');
      }
    }
  }

  /**
   * Change password for the currently authenticated user.
   * Requires re-authentication with current password first.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('You must be logged in with an email account to change your password.');
    }

    if (newPassword.length < 8) {
      throw new Error('New password must be at least 8 characters long.');
    }

    try {
      // Re-authenticate with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update to new password
      await updatePassword(user, newPassword);
    } catch (error: any) {
      console.error('Error changing password:', error);

      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        throw new Error('Current password is incorrect.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('New password is too weak. Please use at least 8 characters with a mix of letters and numbers.');
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('For security, please log out and log back in before changing your password.');
      } else {
        throw new Error('Failed to change password. Please try again.');
      }
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      console.error('Error logging out:', error);
      throw new Error('Logout failed. Please try again.');
    }
  }

  /**
   * Get currently authenticated user
   * @returns Firebase User or null
   */
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  /**
   * Get current user's ID token (for API calls)
   * @returns JWT token string
   */
  async getIdToken(): Promise<string | null> {
    const user = this.getCurrentUser();
    if (!user) return null;

    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  /**
   * Send a team invite email via Firebase email link sign-in.
   * Firebase handles delivery — no SMTP or third-party service needed.
   */
  async sendInviteLink(params: {
    email: string;
    role: string;
    name: string;
    practiceName?: string;
  }): Promise<boolean> {
    const { email, role, name, practiceName } = params;

    try {
      // Mint a server-side invite record first. The role lives on that record,
      // not in the URL — a recipient could otherwise edit role= in their own
      // invite link and self-assign System Administrator.
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        console.error('[Auth] Cannot send an invite while signed out.');
        return false;
      }

      const response = await fetch('/api/send-staff-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          role,
          name,
          practiceName,
          siteUrl: window.location.origin,
        }),
      });

      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        console.error('[Auth] Could not mint invite:', detail.error || response.status);
        return false;
      }

      const { inviteUrl, code } = await response.json();

      await sendSignInLinkToEmail(auth, email, {
        url: inviteUrl,
        handleCodeInApp: true,
      });

      // Prefill for the signup page. The code is the part that authorizes.
      window.localStorage.setItem('inviteEmail', email);
      window.localStorage.setItem('inviteRole', role);
      window.localStorage.setItem('inviteName', name);
      window.localStorage.setItem('inviteCode', code);
      return true;
    } catch (error: any) {
      console.error('[Auth] Failed to send invite link:', error.message);
      return false;
    }
  }

  /**
   * Check if the current URL is a sign-in email link
   */
  isEmailSignInLink(url: string): boolean {
    return isSignInWithEmailLink(auth, url);
  }

  /**
   * Complete sign-in with email link (used on provider-signup when arriving from invite)
   */
  async signInWithEmailLink(email: string, url: string): Promise<UserCredential> {
    return signInWithEmailLink(auth, email, url);
  }

  /**
   * Listen to auth state changes
   * @param callback - Function to call when auth state changes
   * @returns Unsubscribe function
   */
  onAuthStateChanged(callback: (user: User | null) => void): Unsubscribe {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Get user profile from Firestore
   * @param uid - User ID
   * @returns UserProfile or null
   */
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as UserProfile;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Create user profile in Firestore
   * @param user - Firebase User
   * @param role - User role (provider or client) - legacy parameter
   * @param clinicId - Clinic ID (for providers)
   * @param patientId - Patient ID (for clients)
   * @param roles - NEW: Array of roles for multi-role support
   */
  async createUserProfile(
    user: User,
    role: UserRole,
    clinicId?: string,
    patientId?: string,
    roles?: UserRole[]
  ): Promise<void> {
    try {
      // Use provided roles array, or convert legacy single role to array
      const userRoles: UserRole[] = roles || [role];
      const primaryRole: UserRole = roles ? roles[0] : role;

      const profile: Record<string, any> = {
        uid: user.uid,
        roles: userRoles,
        primaryRole: primaryRole,
        role, // Keep legacy field for backward compatibility
        displayName: user.displayName || 'Unknown User',
        createdAt: serverTimestamp() as Timestamp,
        lastLogin: serverTimestamp() as Timestamp,
        isActive: true
      };

      // Only include optional fields if they have values (Firestore rejects undefined)
      if (user.email) profile.email = user.email;
      if (user.phoneNumber) profile.phoneNumber = user.phoneNumber;
      if (clinicId) profile.clinicId = clinicId;
      if (patientId) profile.patientId = patientId;

      await setDoc(doc(db, 'users', user.uid), profile);
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  /**
   * Ensure user profile exists, create if not
   * @param user - Firebase User
   * @param role - User role
   */
  async ensureUserProfile(
    user: User,
    role: UserRole,
    displayNameOverride?: string,
    patientIdOverride?: string
  ): Promise<void> {
    const profile = await this.getUserProfile(user.uid);

    if (!profile) {
      // For phone users, override the display name from patient lookup
      if (displayNameOverride && !user.displayName) {
        // Temporarily patch the user object's displayName for createUserProfile
        // (updateProfile was already called in verifyPhoneOTP)
      }
      await this.createUserProfile(user, role, undefined, patientIdOverride);

      // If we have a display name override and the profile was just created with 'Unknown User',
      // update it with the real name
      if (displayNameOverride) {
        await this.updateUserProfile(user.uid, {
          displayName: displayNameOverride,
          patientId: patientIdOverride,
        } as Partial<UserProfile>);
      }
    } else if (profile.displayName === 'Unknown User' && displayNameOverride) {
      // Fix existing profiles that were created before the phone lookup
      await this.updateUserProfile(user.uid, {
        displayName: displayNameOverride,
        patientId: patientIdOverride,
      } as Partial<UserProfile>);
    }
  }

  /**
   * Ensure a client user profile exists with correct role.
   * Handles legacy accounts that may have no users doc or wrong role.
   */
  async ensureClientProfile(user: User): Promise<void> {
    try {
      const profile = await this.getUserProfile(user.uid);
      if (!profile) {
        // No profile at all — create one
        await this.createUserProfile(user, 'client');
      } else if (!profile.roles?.includes('client') && profile.role !== 'client') {
        // Profile exists but missing client role — ADD it (preserve existing roles)
        const docRef = doc(db, 'users', user.uid);
        const existingRoles: UserRole[] = profile.roles || (profile.role ? [profile.role as UserRole] : []);
        if (!existingRoles.includes('client')) {
          existingRoles.push('client');
        }
        await setDoc(docRef, {
          roles: existingRoles,
        }, { merge: true });
      }
    } catch (error) {
      console.warn('ensureClientProfile failed (non-blocking):', error);
    }
  }

  /**
   * Update user profile
   * @param uid - User ID
   * @param data - Partial profile data to update
   */
  async updateUserProfile(uid: string, data: Partial<UserProfile>): Promise<void> {
    try {
      const docRef = doc(db, 'users', uid);
      await updateDoc(docRef, { ...data });
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  /**
   * Update last login timestamp
   * @param uid - User ID
   */
  async updateLastLogin(uid: string): Promise<void> {
    try {
      const docRef = doc(db, 'users', uid);
      // Use setDoc with merge so it works even if the user doc doesn't exist yet
      await setDoc(docRef, { lastLogin: serverTimestamp() }, { merge: true });
    } catch (error) {
      console.error('Error updating last login:', error);
      // Don't throw - this is not critical
    }
  }

  /**
   * Check if current user has a specific role
   * @param requiredRole - Required role
   * @returns true if user has role
   */
  async hasRole(requiredRole: UserRole | 'provider' | 'client'): Promise<boolean> {
    const user = this.getCurrentUser();
    if (!user) return false;

    const profile = await this.getUserProfile(user.uid);
    if (!profile) return false;

    // Check new multi-role format
    if (profile.roles) {
      return profile.roles.includes(requiredRole as UserRole);
    }

    // Fallback to legacy single role format
    return profile.role === requiredRole;
  }

  /**
   * Add role to user (for multi-role support)
   * @param uid - User ID
   * @param roleToAdd - Role to add
   */
  async addRole(uid: string, roleToAdd: UserRole): Promise<void> {
    try {
      const profile = await this.getUserProfile(uid);
      if (!profile) throw new Error('User profile not found');

      // Get existing roles or convert legacy role
      const currentRoles = profile.roles || (profile.role ? [profile.role] : []);

      // Don't add if already exists
      if (currentRoles.includes(roleToAdd)) {
        return;
      }

      const updatedRoles = [...currentRoles, roleToAdd];

      await this.updateUserProfile(uid, { roles: updatedRoles });
    } catch (error) {
      console.error('Error adding role:', error);
      throw error;
    }
  }

  /**
   * Remove role from user (for multi-role support)
   * @param uid - User ID
   * @param roleToRemove - Role to remove
   */
  async removeRole(uid: string, roleToRemove: UserRole): Promise<void> {
    try {
      const profile = await this.getUserProfile(uid);
      if (!profile) throw new Error('User profile not found');

      const currentRoles = profile.roles || (profile.role ? [profile.role] : []);
      const updatedRoles = currentRoles.filter(r => r !== roleToRemove);

      if (updatedRoles.length === 0) {
        throw new Error('Cannot remove last role from user');
      }

      // Update primary role if we removed it
      let primaryRole = profile.primaryRole;
      if (primaryRole === roleToRemove) {
        primaryRole = updatedRoles[0];
      }

      await this.updateUserProfile(uid, {
        roles: updatedRoles,
        primaryRole: primaryRole
      });
    } catch (error) {
      console.error('Error removing role:', error);
      throw error;
    }
  }

  /**
   * Set user's primary role (default dashboard)
   * @param uid - User ID
   * @param newPrimaryRole - New primary role
   */
  async setPrimaryRole(uid: string, newPrimaryRole: UserRole): Promise<void> {
    try {
      const profile = await this.getUserProfile(uid);
      if (!profile) throw new Error('User profile not found');

      const currentRoles = profile.roles || (profile.role ? [profile.role] : []);

      if (!currentRoles.includes(newPrimaryRole)) {
        throw new Error('Cannot set primary role - user does not have this role');
      }

      await this.updateUserProfile(uid, { primaryRole: newPrimaryRole });
    } catch (error) {
      console.error('Error setting primary role:', error);
      throw error;
    }
  }

  /**
   * Link phone number to existing email account (for providers)
   * Useful if provider wants to enable phone login
   */
  async linkPhoneNumber(phoneNumber: string, confirmationResult: ConfirmationResult): Promise<void> {
    try {
      const user = this.getCurrentUser();
      if (!user) throw new Error('No user logged in');

      // This would use Firebase's linkWithCredential
      // Implementation depends on your specific needs
      throw new Error('Phone linking not yet implemented');
    } catch (error) {
      console.error('Error linking phone number:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;
